import { useState } from 'react';
import { formatHours, toWireDate, type Worklog } from '@forecastify/shared';
import { Badge, Button, Icon } from '@forecastify/ui';
import { useT } from '../../i18n';
import {
  WorklogFields,
  emptyWorklogDraft,
  worklogHours,
  type WorklogDraft,
} from '../time/worklog-form';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The task detail's Időlogok tab, and the log-time widget beside it.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * **C8 deferred this and C11 owes it.** What C8's note said — *"a tab listing nothing would say
 * 'nothing has been logged' when the truth is 'nothing can be logged yet'"* — is now false, so the
 * tab exists.
 *
 * ⚠️⚠️ **THE MOCKUP DECLARES THIS TAB AND DRAWS NO PANEL FOR IT.** `TaskDetail.jsx` 128 puts
 * `{ value: 'logs', label: 'Időlogok', icon: 'timer' }` in the tab strip and sets `isLogs` — and
 * `grep isLogs docs/mockup/` finds **zero** references in any markup. It is one of the 140 keys
 * CLAUDE.md warns about: the tab *button* renders and its contents were never drawn.
 *
 * So the panel is not transcribed, it is **derived**, and from the nearest thing the mockup does
 * draw: the project's Idő tab (`main.jsx` 2635–2650) minus the two columns a task already knows —
 * the task key and the project. What is left is date, note, who and hours, plus the total that
 * every other hour list carries. Stated here so the next reader knows which parts are the mockup's
 * and which are ours.
 */
export function TaskLogs({
  entries,
  trackedHours,
  partial,
  canLog,
  canEdit,
  today,
  onLog,
  overtimeOffered = false,
  onDelete,
}: {
  readonly entries: readonly Worklog[];
  /**
   * The task's total, all time, summed in SQL.
   *
   * ⚠️⚠️ **NOT SUMMED FROM `entries`, AND THAT CHANGED WITH C12's CURSOR.** Adding up the rows in
   * hand was exact only while the endpoint returned all of them. Paged, that sum becomes "the first
   * fifty" wearing the label of a total — which `state-rules.md` §5 forbids in as many words.
   */
  readonly trackedHours: string;
  /** Whether more entries exist than are shown. Said on screen; never silently swallowed. */
  readonly partial: boolean;
  readonly canLog: boolean;
  readonly canEdit: boolean;
  /** Injectable so a test is not at the mercy of the clock. Defaults to today. */
  readonly today?: string;
  /**
   * ⚠️⚠️ **THE WHOLE DRAFT, NOT THREE STRINGS — feedback round 4 item 20.** This widget used
   * to take a date, hours and a note, which meant somebody logging against a ticket could not mark
   * the hour as overtime: the one feature whose only entrance is a worklog form had no entrance
   * from the screen most hours are logged on. `worklog-form.tsx` is the form now, everywhere.
   */
  readonly onLog: (draft: WorklogDraft) => void;
  /**
   * ⚠️ Whether the workspace defines any active overtime type — the form's marking control is
   * absent when it does not. The TYPES themselves are the approver's business since `0147`.
   */
  readonly overtimeOffered?: boolean;
  readonly onDelete: (entry: Worklog) => void;
}) {
  const t = useT();
  const day = today ?? toWireDate(new Date());

  const [draft, setDraft] = useState<WorklogDraft>(() => emptyWorklogDraft(day));

  return (
    <div className="mt-3">
      <div className="flex items-center gap-2 pb-2">
        <span className="text-muted-foreground text-[11.5px] font-medium">
          {t('task.logs.total')}
        </span>
        <Badge variant="secondary">{formatHours(trackedHours)}</Badge>
      </div>

      {entries.map((entry) => (
        <div
          key={entry.id}
          className="flex items-center gap-3 border-b py-1.5 text-[12.5px] last:border-0"
        >
          <span className="text-muted-foreground w-[92px] shrink-0 tabular-nums">
            {entry.workedOn}
          </span>
          {/* ⚠️ An em-dash for an absent note. A blank column reads as a rendering fault. */}
          <span className="min-w-0 flex-1 truncate">{entry.note ?? '–'}</span>
          {/* 30 Oct 2026 · a fixed cell — a name's length moved the column (UAT). */}
          <span className="text-muted-foreground w-[120px] shrink-0 truncate text-[11.5px]">
            {entry.memberName}
          </span>
          <span className="w-[56px] shrink-0 text-right tabular-nums">
            {formatHours(entry.hours)}
          </span>
          {canEdit && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => {
                onDelete(entry);
              }}
              aria-label={t('time.entry.remove')}
            >
              <Icon name="trash" size={13} />
            </Button>
          )}
        </div>
      ))}

      {entries.length === 0 && (
        <p className="text-muted-foreground py-6 text-center text-[12.5px]">
          {t('task.logs.empty')}
        </p>
      )}

      {/* ⚠️ `state-rules.md` §6: a list that pages says so. The total above is still exact, because
          it is an aggregate rather than a sum of what happens to be loaded. */}
      {partial && (
        <p className="text-muted-foreground pt-2 text-[11.5px]">
          {t('task.logs.partial', { count: entries.length })}
        </p>
      )}

      {/**
       * ⚠️ **The log-time widget knows its task, so it asks only what it cannot know.** The Time
       * overview's quick-log has to ask which task; here that is the page. What is left is the day
       * — which defaults to today and is editable, because logging yesterday's work this morning is
       * the ordinary case — the hours, and a note.
       *
       * ⚠️ **Absent rather than disabled without `worklog.create`**, for the reason the quick-log
       * records: a form somebody may never submit should not be drawn.
       */}
      {canLog && (
        <div className="mt-3 border-t pt-3">
          <WorklogFields
            draft={draft}
            onChange={setDraft}
            overtimeOffered={overtimeOffered}
            lead={null}
          />
          <div className="mt-2 flex">
            <div className="flex-1" />
            <Button
              disabled={worklogHours(draft) === null}
              onClick={() => {
                onLog(draft);
                /* ⚠️ Cleared back to a fresh draft, which keeps the DAY somebody chose: logging
                   three entries against yesterday should not send the date back to today twice. */
                setDraft({ ...emptyWorklogDraft(draft.workedOn) });
              }}
            >
              {t('time.log.submit')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
